"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import useAuth from "@/hooks/auth/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { signIn, isAuthenticated, loading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Get redirect destination from URL params (supports both redirectTo and redirectedFrom)
  const redirectTo = searchParams.get("redirectTo") || searchParams.get("redirectedFrom") || "/";
  const authError = searchParams.get("error");

  // Set error from URL params if present
  useEffect(() => {
    if (authError) {
      setError("Authentication error occurred. Please try again.");
    }
  }, [authError]);

  // Simple redirect if user is already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      // User is authenticated, redirect to destination
      router.push(redirectTo);
    }
  }, [isAuthenticated, authLoading, redirectTo, router]);
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading authentication...</p>
        </div>
      </div>
    );
  }
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const result = await signIn(email, password);

      if (result.error) {
        // Handle specific error types with user-friendly messages
        let userMessage = result.error;

        if (result.error.includes("Invalid login credentials")) {
          userMessage = "Invalid email or password. Please check your credentials and try again.";
        } else if (result.error.includes("Email not confirmed")) {
          userMessage = "Please check your email and confirm your account before signing in.";
        } else if (result.error.includes("Too many requests")) {
          userMessage = "Too many login attempts. Please wait a moment before trying again.";
        } else if (result.error.includes("Invalid email")) {
          userMessage = "Please enter a valid email address.";
        }

        setError(userMessage);
        setLoading(false);
      } else {
        // Success will be handled by the auth state change and redirect effect
        console.log("Sign in successful");
        // Keep loading true until redirect happens
      }
    } catch (error) {
      console.error("Sign in error:", error);
      setError("An unexpected error occurred. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex relative"
      style={{
        backgroundImage: "url(/login-bg.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Login Form Container */}
      {/* Mobile: Centered with padding creating blue frame */}
      {/* Tablet/iPad: Centered with padding creating blue frame */}
      {/* Desktop: Left side split-screen (original layout) */}
      <div className="relative z-10 w-full flex items-center justify-center p-4 sm:p-6 md:p-8 lg:w-1/2 lg:justify-start lg:pl-16 lg:p-0 lg:py-8">
        <div className="bg-white rounded-3xl p-8 sm:p-12 md:p-16 lg:p-12 w-full max-w-xl sm:max-w-3xl md:max-w-4xl lg:max-w-md shadow-2xl">
          {/* Header with logo */}
          <div className="mb-6 sm:mb-10 md:mb-12 lg:mb-10">
            <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start lg:items-center lg:justify-start gap-4 sm:gap-5 md:gap-6 lg:gap-4 text-center sm:text-left">
              <img
                src="/images/advanced-logo.png"
                alt="PODHero Logo"
                className="h-12 sm:h-16 md:h-20 lg:h-12 w-auto"
              />
              <div>
                <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-2xl font-bold text-gray-900">
                  Welcome to PODHero
                </h1>
                <p className="text-sm sm:text-lg md:text-xl lg:text-base text-gray-600 mt-1">
                  Manage your print-on-demand operations
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-gray-50 rounded-2xl sm:rounded-3xl md:rounded-3xl lg:rounded-2xl p-6 sm:p-10 md:p-12 lg:p-8">
            <h2 className="text-xl sm:text-3xl md:text-4xl lg:text-2xl font-bold text-gray-900 mb-6 sm:mb-10 md:mb-12 lg:mb-6 text-center sm:text-left">
              Sign In
            </h2>

            <form
              className="space-y-6 sm:space-y-8 md:space-y-10 lg:space-y-6 max-w-3xl lg:max-w-none mx-auto"
              onSubmit={handleSignIn}
            >
              {error && (
                <Alert variant="destructive" className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-800 text-sm sm:text-base md:text-lg lg:text-sm">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <div>
                <Label
                  htmlFor="email"
                  className="block text-sm sm:text-lg md:text-xl lg:text-sm font-medium text-gray-700 mb-2 sm:mb-4 md:mb-5 lg:mb-2"
                >
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  placeholder="johndoe@mail.com"
                  className="h-12 sm:h-14 md:h-16 lg:h-12 border-gray-200 rounded-lg sm:rounded-xl md:rounded-xl lg:rounded-lg focus:border-blue-500 focus:ring-blue-500 bg-white text-base sm:text-lg md:text-xl lg:text-base px-4 sm:px-5 md:px-6 lg:px-4"
                />
              </div>

              <div>
                <Label
                  htmlFor="password"
                  className="block text-sm sm:text-lg md:text-xl lg:text-sm font-medium text-gray-700 mb-2 sm:mb-4 md:mb-5 lg:mb-2"
                >
                  Password <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  placeholder="••••••••"
                  className="h-12 sm:h-14 md:h-16 lg:h-12 border-gray-200 rounded-lg sm:rounded-xl md:rounded-xl lg:rounded-lg focus:border-blue-500 focus:ring-blue-500 bg-white text-base sm:text-lg md:text-xl lg:text-base px-4 sm:px-5 md:px-6 lg:px-4"
                />
                <div className="mt-2 sm:mt-4 md:mt-5 lg:mt-2 text-right">
                  <button
                    type="button"
                    className="text-sm sm:text-lg md:text-xl lg:text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>

              <div className="pt-2 sm:pt-4 md:pt-6 lg:pt-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 sm:h-14 md:h-16 lg:h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium sm:font-semibold lg:font-medium rounded-lg sm:rounded-xl md:rounded-xl lg:rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-base sm:text-lg md:text-xl lg:text-base"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 sm:h-6 sm:w-6 md:h-7 md:w-7 lg:h-4 lg:w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right side - Background visible (Desktop only) */}
      <div className="hidden lg:block lg:w-1/2"></div>
    </div>
  );
}
